import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { SearchableSelectWithQuickAdd } from './ui/searchable-select-quick-add';
import { Plus, Trash2, MapPin, Moon } from 'lucide-react';
import { NumberStepper } from './ui/number-stepper';
import { QuickAddPlaceModal } from './quick-add';

/**
 * CustomPackageBuilder - Handles Tour Route (places + nights) for Custom Packages
 * Travel Dates and Passengers are now handled by common sections in GuestTripStep
 */
const CustomPackageBuilder = ({ 
  places, 
  formData, 
  setFormData,
}) => {
  const [customPackage, setCustomPackage] = useState({
    stops: [], // Place selections with nights
  });
  
  // Quick-Add Place Modal state
  const [quickAddPlaceOpen, setQuickAddPlaceOpen] = useState(false);
  const [quickAddPlacePrefill, setQuickAddPlacePrefill] = useState('');
  const [quickAddPlaceContext, setQuickAddPlaceContext] = useState(null);
  
  // Quick-Add Place handlers
  const handleQuickAddPlace = (searchTerm, context) => {
    setQuickAddPlacePrefill(searchTerm);
    setQuickAddPlaceContext(context);
    setQuickAddPlaceOpen(true);
  };
  
  const handleQuickAddPlaceSuccess = (newPlace) => {
    // Auto-select the new place based on context
    if (typeof quickAddPlaceContext === 'string' && quickAddPlaceContext.startsWith('stop_')) {
      const stopIndex = parseInt(quickAddPlaceContext.replace('stop_', ''));
      updateStopPlace(stopIndex, newPlace.place_name);
    }
  };

  // Sync customPackage with formData on mount
  useEffect(() => {
    if (formData.custom_package_data) {
      setCustomPackage(prev => ({ ...prev, ...formData.custom_package_data }));
    }
  }, []);

  // Update formData when customPackage changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      custom_package_data: customPackage,
    }));
  }, [customPackage, setFormData]);

  // Sync arrival_city, departure_city, and arrival_date from formData to custom_package_data
  useEffect(() => {
    const needsSync = 
      formData.arrival_city !== customPackage.arrival_city ||
      formData.departure_city !== customPackage.departure_city ||
      formData.arrival_date !== customPackage.arrival_date;
    
    if (needsSync) {
      setCustomPackage(prev => ({
        ...prev,
        arrival_city: formData.arrival_city || prev.arrival_city,
        departure_city: formData.departure_city || prev.departure_city,
        arrival_date: formData.arrival_date || prev.arrival_date,
      }));
    }
  }, [formData.arrival_city, formData.departure_city, formData.arrival_date]);

  // Calculate total nights from stops
  const totalNights = customPackage.stops.reduce((sum, stop) => sum + (stop.nights || 0), 0);
  const totalDays = totalNights + 1;

  // Add new stop with nights
  const addStop = () => {
    setCustomPackage(prev => ({
      ...prev,
      stops: [...prev.stops, {
        id: Date.now(),
        place_id: '',
        place_name: '',
        nights: 1,
        order: prev.stops.length + 1
      }]
    }));
  };

  // Remove stop
  const removeStop = (stopIndex) => {
    setCustomPackage(prev => ({
      ...prev,
      stops: prev.stops.filter((_, i) => i !== stopIndex).map((s, i) => ({ ...s, order: i + 1 }))
    }));
  };

  // Update stop place - receives place_name from SearchableSelectWithQuickAdd
  const updateStopPlace = (stopIndex, placeName) => {
    // Find place by place_name since that's what the select uses as value
    const place = (places || []).find(p => p.place_name === placeName);
    setCustomPackage(prev => {
      const newStops = [...prev.stops];
      newStops[stopIndex] = {
        ...newStops[stopIndex],
        place_id: place?.place_id || placeName, // Use place_id if found, otherwise use placeName
        place_name: placeName
      };
      return { ...prev, stops: newStops };
    });
  };

  // Update stop nights
  const updateStopNights = (stopIndex, nights) => {
    setCustomPackage(prev => {
      const newStops = [...prev.stops];
      newStops[stopIndex] = {
        ...newStops[stopIndex],
        nights: Math.max(1, parseInt(nights) || 1)
      };
      return { ...prev, stops: newStops };
    });
  };

  return (
    <div className="space-y-4">
      {/* Tour Route (Places + Nights) */}
      <div className="space-y-4">
        <p className="text-sm text-gray-600">Add the places you want to visit with nights at each location. Hotel selection will be done in the next step.</p>
        
        {customPackage.stops.length === 0 ? (
          <div className="text-center py-6 text-gray-500 border-2 border-dashed rounded-lg">
            <MapPin className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p>No stops added yet. Click "Add Stop" to create your route.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {customPackage.stops.map((stop, stopIndex) => (
              <div key={stop.id} className="p-3 bg-gray-50 rounded-lg border">
                {/* Mobile: Stack vertically, Desktop: Horizontal */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                  {/* Stop Number */}
                  <div className="flex items-center gap-2">
                    <span className="bg-navy text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shrink-0">
                      {stopIndex + 1}
                    </span>
                    <span className="text-sm text-gray-500 sm:hidden">Stop {stopIndex + 1}</span>
                  </div>
                  
                  {/* Place Selection */}
                  <div className="flex-1">
                    <SearchableSelectWithQuickAdd
                      options={(places || [])
                        .filter(p => p.place_name && p.place_name.trim() !== '')
                        .map(p => ({ value: p.place_name, label: p.place_name }))}
                      value={stop.place_name}
                      onValueChange={(v) => updateStopPlace(stopIndex, v)}
                      placeholder="Select destination"
                      searchPlaceholder="Search places..."
                      data-testid={`stop-place-select-${stopIndex}`}
                      quickAdd={{
                        enabled: true,
                        label: "Add new place",
                        buttonLabel: "Add new place",
                        onAdd: (searchTerm) => handleQuickAddPlace(searchTerm, `stop_${stopIndex}`)
                      }}
                    />
                  </div>
                  
                  {/* Nights Stepper */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 sm:hidden">Nights:</span>
                    <NumberStepper
                      value={stop.nights || 1}
                      onChange={(v) => updateStopNights(stopIndex, v)}
                      min={1}
                      max={15}
                    />
                    <span className="text-xs text-gray-500 hidden sm:inline">nights</span>
                  </div>
                  
                  {/* Delete Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    // Ticket #285275 (#4): removeStop() filters by position; stop.id is a
                    // Date.now() timestamp that never matches, so nothing was deleted.
                    onClick={() => removeStop(stopIndex)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 sm:p-2"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        <Button variant="outline" onClick={addStop} className="w-full">
          <Plus className="w-4 h-4 mr-2" />
          Add Stop
        </Button>

        {customPackage.stops.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
              <strong>Route Preview:</strong> {formData.arrival_city || '?'} → {customPackage.stops.map(s => `${s.place_name || '?'} (${s.nights || 1}N)`).join(' → ')} → {formData.departure_city || '?'}
            </div>
            <div className="text-sm font-medium text-indigo-700 bg-indigo-50 p-3 rounded-lg flex items-center gap-2">
              <Moon className="w-4 h-4" />
              <span>Total Duration: {totalNights} Nights / {totalDays} Days</span>
            </div>
          </div>
        )}
      </div>
      
      {/* Quick Add Place Modal */}
      <QuickAddPlaceModal
        open={quickAddPlaceOpen}
        onOpenChange={setQuickAddPlaceOpen}
        prefillName={quickAddPlacePrefill}
        onSuccess={handleQuickAddPlaceSuccess}
      />
    </div>
  );
};

export default CustomPackageBuilder;
